import { useState } from "react";
import { Bookmark, BookmarkCheck, ExternalLink, Quote } from "lucide-react";
import type { ReferenceItem } from "../../api/types.js";
import { CitationDialog } from "../works/CitationDialog.js";
import { workDetailUrl } from "../works/workApi.js";

function verificationLabel(verification: any) {
  if (!verification) return "等待核验中";
  if (verification.label) return verification.label;
  const labels: Record<string, string> = {
    supported: "已支持",
    partially_supported: "部分支持",
    insufficient: "证据不足",
    uncertain: "无法判断",
    failed: "核验失败",
    running: "核验中"
  };
  return labels[verification.verdict || verification.status] || "等待核验中";
}

function verificationReason(verification: any) {
  if (verification?.reason) return verification.reason;
  const state = verification?.verdict || verification?.status;
  if (state === "failed") return "本条引用核验失败，不能按正常可信引用处理。";
  if (state === "insufficient") return "当前证据不足，不能确认正文标注由该来源支持。";
  if (state === "partially_supported") return "这条引用只支持部分表述，需要复核正文和来源。";
  return "这条引用会由后台核验任务独立检查，不影响继续提问。";
}

function authorLine(authors: string[] = []) {
  if (!authors.length) return "作者信息待补充";
  const visible = authors.slice(0, 3).join("，");
  return authors.length > 3 ? `${visible} 等` : visible;
}

function venueMetricLabels(source: ReferenceItem) {
  const metrics = source.venueMetrics || [];
  const byType = new Map<string, ReferenceItem["venueMetrics"] extends Array<infer Metric> | undefined ? Metric : never>();
  for (const metric of metrics) if (!byType.has(metric.type)) byType.set(metric.type, metric);
  const impact = byType.get("impact_factor");
  const jcr = byType.get("jcr_quartile");
  if (impact) {
    return [
      `JIF${impact.period ? ` ${impact.period}` : ""} ${impact.value}`,
      ...(jcr ? [`JCR ${jcr.value}`] : [])
    ];
  }
  const citeScore = byType.get("citescore");
  const citeScoreQuartile = byType.get("citescore_quartile");
  return [
    ...(citeScore ? [`CiteScore${citeScore.period ? ` ${citeScore.period}` : ""} ${citeScore.value}`] : []),
    ...(citeScoreQuartile ? [`CiteScore ${citeScoreQuartile.value}`] : [])
  ];
}

function dateLabel(value?: string | null) {
  if (!value) return "";
  const parsed = new Date(value);
  if (!Number.isFinite(parsed.getTime())) return "";
  return new Intl.DateTimeFormat("zh-CN", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).format(parsed);
}

export function SourceCard({
  source,
  active,
  citationIndex,
  currentSessionId,
  showVerification = false,
  onSave
}: {
  source: ReferenceItem;
  active?: boolean;
  citationIndex?: number;
  currentSessionId?: string | null;
  showVerification?: boolean;
  onSave?: (source: ReferenceItem) => void | Promise<void>;
}) {
  const [citationOpen, setCitationOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const verification = source.verification;
  const isWeb = source.sourceType === "web";
  const index = source.citationIndex || source.index;
  const detailUrl = source.workId ? workDetailUrl(source.workId, source.isSessionBound ? currentSessionId : null) : "";
  const metrics = venueMetricLabels(source);
  const evidenceSnippet = source.snippets?.find((snippet) => snippet.text?.trim());
  const quote = isWeb
    ? source.evidenceFragments?.find((fragment) => fragment.text?.trim())?.text?.trim() || ""
    : evidenceSnippet?.text?.trim() || source.abstract?.trim() || "";
  const quoteKind = isWeb
    ? source.evidenceKind === "retrieved-page" ? "网页证据" : "搜索摘要"
    : evidenceSnippet?.evidenceLevel === "snippet" ? "被引片段" : "摘要片段";

  async function toggleFavorite() {
    if (!onSave || !source.workId || saving) return;
    setSaving(true);
    setSaveError("");
    try {
      await onSave(source);
    } catch {
      setSaveError(source.isSaved ? "取消收藏失败，请重试。" : "收藏失败，请重试。");
    } finally {
      setSaving(false);
    }
  }

  return (
    <article
      className={`source-card ${active ? "active" : ""}`}
      id={`source-${citationIndex || index || source.poolId}`}
      data-source-citation-index={citationIndex || index || undefined}
      tabIndex={-1}
    >
      <div className="source-title">
        {index && <span className="index">{index}.</span>}
        {isWeb && source.url
          ? <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title} <ExternalLink size={13} aria-hidden="true" /></a>
          : detailUrl ? <a href={detailUrl} target="_blank" rel="noreferrer">{source.title}</a> : <span>{source.title}</span>}
      </div>
      {isWeb ? (
        <>
          <div className="source-authors">{source.publisher || source.domain || "网页来源"}</div>
          <div className="source-meta">
            {source.vertical === "news" ? "新闻" : "普通网页"}
            {source.publishedAt ? ` · 发布 ${dateLabel(source.publishedAt)}` : ""}
            {source.retrievedAt ? ` · 获取 ${dateLabel(source.retrievedAt)}` : ""}
          </div>
        </>
      ) : (
        <>
          <div className="source-authors">{authorLine(source.authors)}</div>
          <div className="source-meta">{source.venue || "来源信息待补充"}{source.year ? ` · ${source.year}` : ""}</div>
          {source.recordStatus === "retracted" && <div className="source-status-warning">该论文已撤稿，请谨慎使用</div>}
        </>
      )}
      <div className="source-badges">
        {isWeb ? (
          <span>{source.evidenceKind === "retrieved-page" ? "已读取页面" : "搜索结果摘要"}</span>
        ) : (
          <>
            <button type="button" disabled={!source.workId || saving} aria-pressed={Boolean(source.isSaved)} onClick={() => void toggleFavorite()}>
              {source.isSaved ? <BookmarkCheck size={12} /> : <Bookmark size={12} />}
              {saving ? "处理中…" : source.isSaved ? "已收藏" : "收藏"}
            </button>
            <button type="button" disabled={!source.workId} onClick={() => setCitationOpen(true)}><Quote size={12} /> 引用</button>
            {metrics.map((metric) => <span key={metric}>{metric}</span>)}
          </>
        )}
      </div>
      {quote && (
        <details className="source-quote" open>
          <summary><span>{quoteKind}</span><small>证据</small></summary>
          {isWeb
            ? source.evidenceFragments?.map((fragment, fragmentIndex) => <p key={`${source.poolId}-evidence-${fragmentIndex}`}>{fragment.text}</p>)
            : <p>{quote}</p>}
        </details>
      )}
      {!isWeb && saveError && <p className="source-action-error" role="alert">{saveError}</p>}
      {!isWeb && showVerification && (
        <div className={`source-verification ${verification?.verdict || verification?.status || "pending"}`}>
          <strong>引用核验</strong>
          <span>{verificationLabel(verification)}</span>
          <p>{verificationReason(verification)}</p>
        </div>
      )}
      {source.workId && <CitationDialog workId={source.workId} title={source.title} open={citationOpen} onClose={() => setCitationOpen(false)} />}
    </article>
  );
}
